import { useRef, useState } from "react";

import { uploadFile, deleteClip } from "../../api/media";
import type { MediaClip } from "../../api/media";
import { useEditor } from "../../store/editor";

/** Formato del payload de drag interno entre mediateca y timeline. */
export interface MediaDragPayload {
  kind: "media";
  mediaId: number;
  mediaType: "video" | "audio" | "image";
  durationMs: number;
  name: string;
}
export const MEDIA_DRAG_MIME = "application/x-videotool-media";

export default function MediaLibrary(props: {
  projectId: number;
  library: MediaClip[];
  onRefresh: () => void;
}) {
  const { projectId, library: clips, onRefresh } = props;
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [dropHover, setDropHover] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const addVideo = useEditor((s) => s.addVideoClip);
  const addAudio = useEditor((s) => s.addAudioClip);

  async function uploadMany(files: File[]) {
    for (const f of files) {
      const key = `${f.name}:${f.size}`;
      setProgress((p) => ({ ...p, [key]: 0 }));
      try {
        await uploadFile(projectId, f, (pct) => setProgress((p) => ({ ...p, [key]: pct })));
      } catch (err) {
        console.error(err);
      } finally {
        setProgress((p) => { const c = { ...p }; delete c[key]; return c; });
        onRefresh();
      }
    }
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    uploadMany(files);
  }

  function onAddToTimeline(c: MediaClip) {
    if (c.status !== "ready") return;
    if (c.type === "audio") addAudio(c.id, c.duration_ms, c.name);
    else addVideo(c.id, c.duration_ms, c.name);
  }

  async function onDelete(c: MediaClip, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm(`Borrar "${c.name}"?`)) return;
    await deleteClip(c.id);
    onRefresh();
  }

  function onItemDragStart(e: React.DragEvent, c: MediaClip) {
    if (c.status !== "ready") { e.preventDefault(); return; }
    const payload: MediaDragPayload = {
      kind: "media",
      mediaId: c.id,
      mediaType: c.type,
      durationMs: c.duration_ms,
      name: c.name,
    };
    e.dataTransfer.setData(MEDIA_DRAG_MIME, JSON.stringify(payload));
    // MIME extra para que el timeline conozca el tipo en dragover (donde getData está restringido)
    e.dataTransfer.setData(`application/x-videotool-media+${c.type}`, "1");
    e.dataTransfer.effectAllowed = "copy";
  }

  function onPanelDragOver(e: React.DragEvent) {
    // Sólo aceptamos archivos del SO
    if (e.dataTransfer.types.includes("Files")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setDropHover(true);
    }
  }
  function onPanelDragLeave(e: React.DragEvent) {
    // dragleave fluctúa al pasar por hijos; chequeamos que salió del panel
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDropHover(false);
  }
  function onPanelDrop(e: React.DragEvent) {
    setDropHover(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) {
      e.preventDefault();
      uploadMany(files);
    }
  }

  return (
    <div
      onDragOver={onPanelDragOver}
      onDragLeave={onPanelDragLeave}
      onDrop={onPanelDrop}
      style={{
        height: "100%", position: "relative",
        outline: dropHover ? "2px dashed var(--accent)" : "none",
        outlineOffset: -3,
      }}
    >
      <div className="panel-header">
        <span>Mediateca</span>
        <button onClick={() => inputRef.current?.click()} style={{ fontSize: 11, padding: "3px 8px" }}>
          + Subir
        </button>
        <input ref={inputRef} type="file" multiple
               accept="video/*,audio/*,image/*"
               style={{ display: "none" }} onChange={onPick} />
      </div>
      {Object.entries(progress).map(([k, v]) => (
        <div key={k} style={{ padding: "4px 14px", fontSize: 11, color: "var(--text-dim)" }}>
          {k.split(":")[0]} — {v}%
        </div>
      ))}
      <div className="media-list">
        {clips.map((c) => (
          <div
            key={c.id}
            className="media-item"
            draggable={c.status === "ready"}
            onDragStart={(e) => onItemDragStart(e, c)}
            onClick={() => onAddToTimeline(c)}
            title="Doble click o arrastra al timeline"
          >
            {c.thumb_url ? <img src={c.thumb_url} alt="" />
              : <div style={{ aspectRatio: "16 / 9", background: "#000",
                              display: "grid", placeItems: "center", color: "var(--text-dim)" }}>
                  {c.type === "audio" ? "♪" : "·"}
                </div>}
            <div className="meta">
              <div title={c.name} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {c.name}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                <span className={`status-tag status-${c.status}`}>{c.status}</span>
                {c.duration_ms > 0 && <span>{(c.duration_ms / 1000).toFixed(1)}s</span>}
              </div>
              <button className="danger" onClick={(e) => onDelete(c, e)}
                      style={{ marginTop: 4, fontSize: 10, padding: "1px 6px" }}>borrar</button>
            </div>
          </div>
        ))}
      </div>

      {dropHover && (
        <div style={{
          position: "absolute", inset: 0,
          background: "rgba(91,199,255,0.08)",
          display: "grid", placeItems: "center",
          fontSize: 14, color: "var(--accent)", fontWeight: 600,
          pointerEvents: "none",
        }}>
          Suelta para subir
        </div>
      )}
    </div>
  );
}
